import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Routes } from '../../config/routes';
import { JwtLegFiClaims } from '../auth/jwt-legfi-claims.model';
import { LegFiJwtService } from '../auth/legfi-jwt.service';
import { ApplicationHttpClient } from '../../components/shared/http/application-http-client';
import { Category, CATEGORY_EXPENSE_ACCOUNT_TYPE, CATEGORY_INCOME_ACCOUNT_TYPE } from '../../models/entities/accounting/category';
import { map, tap } from 'rxjs/operators';
import { Accounting } from 'app/components/shared/http/request-interfaces';

export interface CategoryStreams
{
    $loaded: BehaviorSubject<boolean>;
    $loading: BehaviorSubject<boolean>;
    $categories: BehaviorSubject<Category[]>;
    $revenueCategories: Observable<Category[]>;
    $expenseCategories: Observable<Category[]>;
    $activeRevenueCategories: Observable<Category[]>;
    $activeExpenseCategories: Observable<Category[]>;
    $active: Observable<Category[]>;
}

@Injectable({
    providedIn: 'root',
})
export class CategoriesService
{
    private _categories: { [orgId: number]: Category[] } = {};
    private _orgStreams: {
        [orgId: number]: CategoryStreams
    } = {};

    constructor(private _http: ApplicationHttpClient) {
    }

    /**
     * Clears all per-organization category caches and streams.
     *
     * This service is providedIn:'root', so its per-org caches live for the JS app lifetime. A
     * normal org switch does a full page reload (wiping it), but an in-app logout via Angular
     * navigation (AuthService.logout with preserveLocation) does NOT — leaving a prior org's
     * categories cached for the next session. Called on logout so categories are always re-fetched
     * for the active org (SD-7271/7407/7474).
     */
    public clearCache(): void {
        this._categories = {};
        this._orgStreams = {};
    }

    getOrganizationStreams(organizationId: number) {
        if (this._orgStreams[organizationId]) {
            return this._orgStreams[organizationId];
        }

        const $categories = new BehaviorSubject<Category[]>([]);
        const streams = {
            $loaded: new BehaviorSubject<boolean>(false),
            $loading: new BehaviorSubject<boolean>(false),
            $categories: $categories,
            $revenueCategories: $categories.pipe(map(categories => categories.filter(category => category.type === CATEGORY_INCOME_ACCOUNT_TYPE))),
            $expenseCategories: $categories.pipe(map(categories => categories.filter(category => category.type === CATEGORY_EXPENSE_ACCOUNT_TYPE))),
            $activeRevenueCategories: $categories.pipe(map((categories) => {
                return categories.filter((category) => {
                    if (!category.deletedAt) {
                        category.children = category.children.filter((cate) => !cate.deletedAt);
                        return category.type === CATEGORY_INCOME_ACCOUNT_TYPE;
                    }
                    return false;
                });
            })),
            $activeExpenseCategories: $categories.pipe(map((categories) => {
                return categories.filter((category) => {
                    if (!category.deletedAt) {
                        category.children = category.children.filter((cate) => !cate.deletedAt);
                        return category.type === CATEGORY_EXPENSE_ACCOUNT_TYPE;
                    }
                    return false;
                });
            })),
            $active: $categories.pipe(map((categories) => {
                return categories.filter((category) => {
                    if (!category.deletedAt) {
                        category.children = category.children.filter((cate) => !cate.deletedAt);
                        return true;
                    }
                    return false;
                });
            })),
        };
        this._orgStreams[organizationId] = streams;
        return streams;
    }

    /**
     * Gets all categories for specified organization.
     * @param {number} organizationId
     * @param {boolean} forceRefresh - If true, bypasses cache and shows loading indicator
     * @returns {any}
     */
    public getAllCategories(organizationId: number, forceRefresh: boolean = false): Observable<Category[]> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        organizationId = organizationId || jwt.orgId;
        const streams = this.getOrganizationStreams(organizationId);

        // If already loaded and not forcing refresh, return cached data silently and refresh in background
        if (streams.$loaded.value && !forceRefresh) {
            // Refresh in background without showing loading indicator
            const url: string = Routes.MakeLegFiCoreUrl(Routes.AccountingV2Module.Categories(organizationId || jwt.orgId));
            this._http.get(url).pipe(map((response: Object[]) => {
                const categories = response.map(cat => new Category(cat));
                this._categories[organizationId] = categories;
                streams.$categories.next(categories);
                return categories;
            })).subscribe();

            // Return immediately with cached data
            return streams.$categories.asObservable();
        }

        // First load or forced refresh - show loading indicator
        const url: string = Routes.MakeLegFiCoreUrl(Routes.AccountingV2Module.Categories(organizationId || jwt.orgId));
        streams.$loading.next(true);
        return this._http.get(url).pipe(map((response: Object[]) => {
            const categories = response.map(cat => new Category(cat));
            this._categories[organizationId] = categories;
            streams.$categories.next(categories);
            streams.$loading.next(false);
            streams.$loaded.next(true);
            return categories;
        }));
    }

    /**
     * Create a new category record for specified organization.
     * @param {number} organizationId
     * @param {any} requestBody
     * @returns {any}
     */
    public createCategory(
            organizationId: number,
            requestBody: Accounting.Categories.StoreCategoryRequest,
    ): Observable<Category> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }
        organizationId = organizationId || jwt.orgId;

        return this._http.post(
                Routes.MakeLegFiCoreUrl(
                        Routes.AccountingV2Module.Categories(organizationId),
                ),
                JSON.stringify(requestBody),
        ).pipe(map((response) => {
            return new Category(response);
        })).pipe(tap((category: Category) => {
            const cats = this._categories[organizationId];
            if (category.parentId) {
                const parent = cats.find(c => c.id === category.parentId);
                if (parent) {
                    parent.children.push(category);
                }
            } else {
                this._categories[organizationId].push(category);
            }
            const streams = this.getOrganizationStreams(organizationId);
            streams.$categories.next(this._categories[organizationId]);
        }));
    }

    /**
     * Get the number of transactions and charges that a given category is used on
     * @param {number} organizationId
     * @param {number} categoryId
     * @returns {any}
     */
    public getCategoryItemCount(organizationId: number, categoryId: number): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.CategoryItemCount(organizationId || jwt.orgId, categoryId),
        );

        return this._http.get(url);
    }

    /**
     * Delete a category record for a specified organization.
     * @param {number} organizationId
     * @param {number} categoryId
     * @param replaceWith
     * @returns {any}
     */
    public deleteCategory(organizationId: number, categoryId: number, replaceWith?: number): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(Routes.AccountingV2Module.DeleteCategory(organizationId || jwt.orgId, categoryId, replaceWith));

        return this._http.delete(url);
    }

    /**
     * Update a category record for a specified organization.
     * @param {number} organizationId
     * @param {number} categoryId
     * @param {any} requestBody
     * @returns {any}
     */
    public updateCategory(
            organizationId: number,
            categoryId: number,
            requestBody: Accounting.Categories.PutCategoryRequest,
    ): Observable<Category> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }
        organizationId = organizationId || jwt.orgId;

        return this._http.put(
                Routes.MakeLegFiCoreUrl(
                        Routes.AccountingV2Module.Category(organizationId, categoryId),
                ),
                JSON.stringify(requestBody),
        ).pipe(map((response: Object) => {
            return new Category(response);
        }));
    }

    /**
     * Reorder categories in the specified organization.
     * @param {number} organizationId
     * @param {number[]} categoryIds - Array of category IDs in the desired display order
     * @returns {Observable<void>}
     */
    public reorderCategories(
            organizationId: number,
            categoryIds: number[],
    ): Observable<void> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }
        organizationId = organizationId || jwt.orgId;

        const requestBody: Accounting.Categories.ReorderCategoriesRequest = {
            categoryIds: categoryIds,
        };

        return this._http.put(
                Routes.MakeLegFiCoreUrl(
                        Routes.AccountingV2Module.ReorderCategories(organizationId),
                ),
                JSON.stringify(requestBody),
        ).pipe(map(() => void 0));
    }

    /**
     * Reorder categories using a tree structure.
     * Handles both ordering and parent relationships in a single call.
     */
    public reorderCategoriesTree(
            organizationId: number,
            categoryTree: Array<{ id: number; children: number[] }>,
    ): Observable<void> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }
        organizationId = organizationId || jwt.orgId;

        const requestBody = {
            categories: categoryTree,
        };

        return this._http.put(
                Routes.MakeLegFiCoreUrl(
                        Routes.AccountingV2Module.ReorderCategoriesTree(organizationId),
                ),
                JSON.stringify(requestBody),
        ).pipe(map(() => void 0));
    }

    /**
     * Restore a soft-deleted category.
     *
     * @param organizationId - Organization ID
     * @param categoryId - Category ID to restore
     * @returns Observable of the restored category
     */
    public restoreCategory(organizationId: number, categoryId: number): Observable<Category> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
            Routes.AccountingV2Module.Category(organizationId || jwt.orgId, categoryId) + '/restore'
        );

        return this._http.patch(url, JSON.stringify({})).pipe(
            map((response: Object) => new Category(response))
        );
    }

    /**
     * Bulk set parent for multiple categories.
     * Internal-use categories and categories with children are skipped by the API.
     *
     * @param organizationId - Organization ID
     * @param categoryIds - Array of category IDs to update
     * @param parentId - New parent category ID, or null to make top-level
     * @returns Observable with the count of updated categories
     */
    public bulkSetParent(
        organizationId: number,
        categoryIds: number[],
        parentId: number | null,
    ): Observable<{ success: boolean; updatedCount: number }> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }
        organizationId = organizationId || jwt.orgId;

        const requestBody: Accounting.Categories.BulkSetParentCategoryRequest = {
            categoryIds,
            parentId,
        };

        return this._http.put(
            Routes.MakeLegFiCoreUrl(
                Routes.AccountingV2Module.BulkSetParentCategory(organizationId),
            ),
            JSON.stringify(requestBody),
        ).pipe(map((response: { success: boolean; updatedCount: number }) => response));
    }

    /**
     * Bulk delete multiple categories.
     * Internal-use and already-deleted categories are skipped by the API.
     *
     * @param organizationId - Organization ID
     * @param categoryIds - Array of category IDs to delete
     * @returns Observable with the count of deleted categories
     */
    public bulkDeleteCategories(
        organizationId: number,
        categoryIds: number[],
    ): Observable<{ success: boolean; deletedCount: number }> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }
        organizationId = organizationId || jwt.orgId;

        const url: string = Routes.MakeLegFiCoreUrl(
            Routes.AccountingV2Module.BulkDeleteCategories(organizationId),
        );

        return this._http.delete<{ success: boolean; deletedCount: number }>(url, { body: { ids: categoryIds } });
    }
}
